import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { Trash, UploadSimple, X } from '@phosphor-icons/react';
import React, { useEffect, useRef, useState } from 'react';
import { useDarkMode } from '../../../../hooks/useDarkMode';
import { useSidebarData } from '../../../../hooks/useSidebarData';
import { useUploadItems } from './hooks/useUploadItems';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const { spaces } = useSidebarData();

  const activeSpaces = spaces.filter(s => !s.disabled);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>('private');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const spaceIdForHook =
    selectedSpaceId === 'private' ? undefined : selectedSpaceId;

  const { isUploading, uploadFile } = useUploadItems(spaceIdForHook, {
    onProcessedSuccess: () => {
      setPendingFile(null);
      onClose();
    },
  });

  useEffect(() => {
    if (!open) {
      setSelectedSpaceId('private');
      setPendingFile(null);
    }
  }, [open]);

  const textPrimary = isDarkMode ? palette.gray[100] : palette.gray[900];
  const textSecondary = isDarkMode ? palette.gray[400] : palette.gray[500];
  const mutedSection = isDarkMode ? palette.gray[500] : palette.gray[500];
  const paperBg = isDarkMode ? palette.gray[900] : '#fff';
  const borderSubtle = isDarkMode ? palette.gray[700] : palette.gray[300];
  const cardHoverBg = isDarkMode ? palette.gray[800] : palette.gray[100];
  const dropzoneBg = isDarkMode ? palette.gray[800] : palette.gray[50];

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setPendingFile(file);
  };

  const handleSubmitUpload = () => {
    if (!pendingFile) return;
    void uploadFile(pendingFile);
  };

  const handleCancel = () => {
    setPendingFile(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      TransitionComponent={Grow}
      TransitionProps={{
        style: { transformOrigin: 'center center' },
        timeout: { enter: 240, exit: 220 },
        easing: {
          enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
          exit: 'cubic-bezier(0.22, 1, 0.36, 1)',
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: paperBg,
          backgroundImage: 'none',
          border: `1px solid ${borderSubtle}`,
          borderRadius: 2,
          width: 400,
          maxWidth: '96vw',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <UploadSimple
            size={18}
            weight='bold'
            color={theme.palette.primary.main}
          />
          <Typography
            sx={{ fontWeight: 600, fontSize: 15, color: textPrimary }}
          >
            Upload File
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: textSecondary }}>
          Select a destination and choose a file to upload.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: '16px !important' }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: mutedSection,
            mb: 1,
          }}
        >
          Destination
        </Typography>
        <Select
          value={selectedSpaceId}
          onChange={e => setSelectedSpaceId(e.target.value)}
          fullWidth
          size='small'
          disabled={isUploading}
          sx={{
            fontSize: '14px',
            color: textPrimary,
            backgroundColor: dropzoneBg,
            borderRadius: '8px',
            mb: 2,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: borderSubtle,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDarkMode ? palette.gray[500] : palette.gray[400],
            },
          }}
        >
          <MenuItem value='private'>Private</MenuItem>
          {activeSpaces.map(space => (
            <MenuItem key={space.id} value={space.id}>
              {space.name}
            </MenuItem>
          ))}
        </Select>

        <input
          type='file'
          hidden
          ref={fileInputRef}
          onChange={handleFileInputChange}
        />

        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: mutedSection,
            mb: 1,
          }}
        >
          File
        </Typography>
        <Box
          onClick={() =>
            !isUploading && !pendingFile && fileInputRef.current?.click()
          }
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: pendingFile ? 2 : 3,
            px: 2,
            border: `2px dashed ${borderSubtle}`,
            borderRadius: 2,
            backgroundColor: dropzoneBg,
            cursor: isUploading || pendingFile ? 'default' : 'pointer',
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
            '&:hover':
              isUploading || pendingFile
                ? {}
                : {
                    borderColor: isDarkMode
                      ? palette.gray[500]
                      : palette.gray[400],
                    bgcolor: cardHoverBg,
                  },
          }}
        >
          {isUploading ? (
            <>
              <CircularProgress size={22} />
              <Typography sx={{ fontSize: 12, color: textSecondary }}>
                Uploading...
              </Typography>
            </>
          ) : pendingFile ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  color: textPrimary,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={pendingFile.name}
              >
                {pendingFile.name}
              </Typography>
              <IconButton
                size='small'
                aria-label='Remove file'
                onClick={e => {
                  e.stopPropagation();
                  setPendingFile(null);
                }}
                sx={{ color: textSecondary, flexShrink: 0 }}
              >
                <Trash size={18} />
              </IconButton>
            </Box>
          ) : (
            <>
              <UploadSimple size={26} color={textSecondary} />
              <Typography sx={{ fontSize: 12, color: textSecondary }}>
                Click to select a file
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: borderSubtle }} />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant='outlined'
          disabled={isUploading}
          startIcon={<X size={14} weight='bold' />}
          sx={{
            textTransform: 'none',
            borderColor: borderSubtle,
            color: textPrimary,
            '&:hover': {
              borderColor: isDarkMode ? palette.gray[600] : palette.gray[400],
              bgcolor: cardHoverBg,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmitUpload}
          variant='contained'
          disabled={!pendingFile || isUploading}
          startIcon={
            isUploading ? (
              <CircularProgress size={14} color='inherit' />
            ) : (
              <UploadSimple size={14} weight='bold' />
            )
          }
          sx={{ textTransform: 'none' }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};
